import type { Kontakt, Kontaktbereich } from '@nac2s/shared'
import { kontaktname } from '@nac2s/shared'
import { useCallback, useEffect, useState } from 'react'
import { Input } from '@/components/base/input/input'
import { Knopf } from '@/teile/Knopf'
import { Reiter, Reiterleiste } from '@/teile/Reiter'
import { Fehler, Laedt, Leer, Tabelle } from '@/teile/Tabelle'
import { ApiError } from '../api/client.js'
import { kontaktLoeschen, ladeKontakte } from '../api/kontakte.js'
import { Seitenkopf } from '../layout/AppShell.js'
import { useSitzung } from '../session/SitzungContext.js'
import { KontaktFormular } from '../teile/KontaktFormular.js'
import { KontaktImport } from '../teile/KontaktImport.js'

/**
 * Kontakte (ADR-0016) — eigene und zentrale.
 *
 * Nur, wenn die Edition die Kontaktdatenbank trägt (ADR-0014). Der Eintrag
 * steht immer in der Navigation: Wer ihn öffnet, soll erfahren, dass es
 * Kontakte gibt und was dafür nötig ist. Durchgesetzt wird die Edition am
 * Endpunkt, nicht hier (SECURITY.md §4) — ebenso das Recht, die zentralen zu
 * pflegen.
 */

type Zustand =
  | { status: 'laedt' }
  | { status: 'da'; kontakte: Kontakt[] }
  | { status: 'fehler'; meldung: string }

export function Kontakte() {
  const { zustand: sitzung, hatRecht } = useSitzung()
  const [bereich, setBereich] = useState<Kontaktbereich>('eigene')
  const [suche, setSuche] = useState('')
  const [zustand, setZustand] = useState<Zustand>({ status: 'laedt' })
  const [formular, setFormular] = useState<{ kontakt?: Kontakt } | null>(null)
  const [importOffen, setImportOffen] = useState(false)
  const [arbeitetAn, setArbeitetAn] = useState<string | null>(null)

  const traegtKontakte =
    sitzung.status === 'angemeldet' && sitzung.sitzung.funktionen.includes('kontakte')

  const laden = useCallback(async () => {
    setZustand({ status: 'laedt' })
    try {
      setZustand({ status: 'da', kontakte: await ladeKontakte(bereich, suche) })
    } catch (fehler) {
      setZustand({
        status: 'fehler',
        meldung: fehler instanceof ApiError ? fehler.message : 'Kontakte nicht abrufbar.',
      })
    }
  }, [bereich, suche])

  useEffect(() => {
    if (!traegtKontakte) return
    const t = setTimeout(() => void laden(), suche ? 250 : 0)
    return () => clearTimeout(t)
  }, [laden, traegtKontakte, suche])

  if (sitzung.status !== 'angemeldet') return null
  const beisatz = `Edition ${sitzung.sitzung.editionName}`

  if (!traegtKontakte) {
    return (
      <div>
        <Seitenkopf
          beisatz={beisatz}
          titel="Kontakte"
          satz="Eigene und zentrale Kontakte gehören nicht zu Ihrer Edition."
        />
        <div className="px-6 pb-8">
          <Leer titel="Nicht in Ihrer Edition enthalten">
            Ihr Partner kann Ihr Unternehmen auf eine Edition mit Kontaktdatenbank umstellen —
            danach erscheinen Ihre Kontakte hier.
          </Leer>
        </div>
      </div>
    )
  }

  const darfPflegen =
    bereich === 'eigene' ? hatRecht('contacts.own') : hatRecht('contacts.central.write')

  async function loeschen(k: Kontakt) {
    setArbeitetAn(k.id)
    try {
      await kontaktLoeschen(bereich, k.id)
      await laden()
    } catch {
      setZustand({ status: 'fehler', meldung: 'Der Kontakt ließ sich nicht löschen.' })
    } finally {
      setArbeitetAn(null)
    }
  }

  return (
    <div>
      <Seitenkopf
        beisatz={beisatz}
        titel="Kontakte"
        satz="Ihre eigenen Kontakte und die zentralen Ihres Unternehmens."
      />

      <div className="flex flex-col gap-4 px-6 pb-8">
        <Reiterleiste>
          <Reiter an={bereich === 'eigene'} onWahl={() => setBereich('eigene')}>
            Eigene
          </Reiter>
          <Reiter an={bereich === 'zentral'} onWahl={() => setBereich('zentral')}>
            Zentral
          </Reiter>
        </Reiterleiste>

        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-[16rem] flex-1">
            <Input
              aria-label="Kontakte durchsuchen"
              size="sm"
              placeholder="Name, Firma oder Nummer suchen …"
              value={suche}
              onChange={setSuche}
            />
          </div>
          {darfPflegen ? (
            <span className="flex gap-2">
              <Knopf onClick={() => setImportOffen(true)}>Importieren</Knopf>
              <Knopf stark onClick={() => setFormular({})}>
                Kontakt anlegen
              </Knopf>
            </span>
          ) : null}
        </div>

        {zustand.status === 'laedt' ? (
          <Laedt>Kontakte werden geladen …</Laedt>
        ) : zustand.status === 'fehler' ? (
          <Fehler>{zustand.meldung}</Fehler>
        ) : zustand.kontakte.length === 0 ? (
          <Leer titel={suche ? 'Nichts gefunden' : 'Noch keine Kontakte'}>
            {suche
              ? 'Kein Kontakt passt zu dieser Suche.'
              : bereich === 'eigene'
                ? 'Legen Sie Ihren ersten Kontakt an oder importieren Sie eine CSV-Datei.'
                : 'Im zentralen Verzeichnis steht noch nichts.'}
          </Leer>
        ) : (
          <Tabelle kopf={['Name', 'Firma', 'Position', 'Handy', 'Büro', 'E-Mail', '']}>
            {zustand.kontakte.map((k) => (
              <tr key={k.id} className="border-b border-secondary last:border-b-0">
                <td className="py-3 pr-4 font-medium text-primary">{kontaktname(k)}</td>
                <td className="py-3 pr-4 text-sm text-tertiary">{k.firma ?? '—'}</td>
                <td className="py-3 pr-4 text-sm text-tertiary">{k.position ?? '—'}</td>
                <td className="py-3 pr-4 text-sm text-tertiary tabular-nums">{k.mobil ?? '—'}</td>
                <td className="py-3 pr-4 text-sm text-tertiary tabular-nums">{k.buero ?? '—'}</td>
                <td className="py-3 pr-4 text-sm text-tertiary">{k.email ?? '—'}</td>
                <td className="py-3 text-right">
                  {darfPflegen ? (
                    <span className="inline-flex gap-1.5">
                      <Knopf
                        disabled={arbeitetAn === k.id}
                        onClick={() => setFormular({ kontakt: k })}
                      >
                        Ändern
                      </Knopf>
                      <Knopf gefahr disabled={arbeitetAn === k.id} onClick={() => void loeschen(k)}>
                        Löschen
                      </Knopf>
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </Tabelle>
        )}
      </div>

      {formular ? (
        <KontaktFormular
          bereich={bereich}
          {...(formular.kontakt ? { kontakt: formular.kontakt } : {})}
          onFertig={() => {
            setFormular(null)
            void laden()
          }}
          onAbbruch={() => setFormular(null)}
        />
      ) : null}

      {importOffen ? (
        <KontaktImport
          bereich={bereich}
          onFertig={() => void laden()}
          onAbbruch={() => setImportOffen(false)}
        />
      ) : null}
    </div>
  )
}
